import type { CockpitCaseSummary } from "@/lib/api";

interface Props {
  cases: CockpitCaseSummary[];
}

export function DeadlineProgressBar({ cases }: Props) {
  const buckets = cases.reduce(
    (acc, c) => {
      const d = c.days_remaining;
      if (d === null) acc.unknown += 1;
      else if (d <= 7) acc.critical += 1;
      else if (d <= 14) acc.urgent += 1;
      else if (d <= 30) acc.soon += 1;
      else acc.ok += 1;
      return acc;
    },
    { critical: 0, urgent: 0, soon: 0, ok: 0, unknown: 0 },
  );

  const total = cases.length || 1;

  const segments: { color: string; count: number; label: string }[] = [
    { color: "var(--block-red)", count: buckets.critical, label: "≤7j" },
    { color: "var(--alert-orange)", count: buckets.urgent, label: "8-14j" },
    { color: "var(--proof-gold)", count: buckets.soon, label: "15-30j" },
    {
      color: "var(--arbitre-slate-500)",
      count: buckets.ok,
      label: "+30j",
    },
    {
      color: "var(--graphite-600)",
      count: buckets.unknown,
      label: "n/c",
    },
  ];

  return (
    <div className="space-y-2">
      <div
        className="flex h-2 rounded-full overflow-hidden"
        style={{ background: "var(--graphite-700)" }}
      >
        {segments.map((s) =>
          s.count === 0 ? null : (
            <div
              key={s.label}
              title={`${s.label}: ${s.count}`}
              style={{
                width: `${(s.count / total) * 100}%`,
                background: s.color,
              }}
            />
          ),
        )}
      </div>
      <div className="flex flex-wrap gap-3 text-[11px]">
        {segments.map((s) =>
          s.count === 0 ? null : (
            <div
              key={s.label}
              className="inline-flex items-center gap-1.5"
              style={{ color: "var(--arbitre-slate-300)" }}
            >
              <span
                className="inline-block w-2 h-2 rounded-full"
                style={{ background: s.color }}
              />
              {s.label} ({s.count})
            </div>
          ),
        )}
      </div>
    </div>
  );
}
